/**
 * PolicyEvaluationsTab — recent evaluation audit rows (read-only).
 *
 * Mirrors `business_policy_evaluations` rows. Deny/error rows get a
 * color+icon status; allow rows are subdued.
 */
import { CheckCircle, Warning, XCircle } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader } from '../../../shared/ui/Card';
import { usePolicyEvaluations } from '../../../features/business-policies/useBusinessPolicies';
import type { PolicyEvaluationRecord } from '../../../features/business-policies/types';

export function PolicyEvaluationsTab({
  policyId,
}: {
  policyId: string;
}): JSX.Element {
  const { t } = useTranslation('adminPolicies');
  const query = usePolicyEvaluations(policyId);

  if (query.isLoading) {
    return (
      <Card className="px-biz-card-lg py-biz-section" aria-busy="true">
        <div className="h-4 w-1/3 bg-biz-muted rounded animate-pulse mb-2" />
        <div className="h-4 w-1/2 bg-biz-muted rounded animate-pulse" />
      </Card>
    );
  }

  const rows = query.data ?? [];
  if (rows.length === 0) {
    return (
      <Card className="px-biz-card-lg py-biz-section text-center text-biz-muted-foreground">
        {t('evaluations.empty')}
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div>
          <h3 className="biz-heading font-semibold">{t('evaluations.title')}</h3>
          <p className="text-biz-sm text-biz-muted-foreground">
            {t('evaluations.subtitle')}
          </p>
        </div>
      </CardHeader>
      <ul className="divide-y divide-biz-border">
        {rows.map((r) => (
          <EvalRow key={r.id} row={r} />
        ))}
      </ul>
    </Card>
  );
}

function EvalRow({ row }: { row: PolicyEvaluationRecord }): JSX.Element {
  const { t } = useTranslation('adminPolicies');
  const icon =
    row.result === 'allow' ? (
      <CheckCircle size={18} weight="regular" aria-hidden="true" className="text-biz-success" />
    ) : row.result === 'deny' ? (
      <XCircle size={18} weight="regular" aria-hidden="true" className="text-biz-danger" />
    ) : (
      <Warning size={18} weight="regular" aria-hidden="true" className="text-biz-warning" />
    );
  return (
    <li className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] items-center gap-biz-content-lg px-biz-card py-biz-content-lg">
      <span className="shrink-0">{icon}</span>
      <div className="min-w-0">
        <div className="flex items-center gap-biz-content flex-wrap">
          <span className="font-semibold">{row.tenantName}</span>
          <span className="font-mono text-biz-xs text-biz-muted-foreground">
            {row.action}
          </span>
          <span className="font-mono text-biz-xs text-biz-muted-foreground">
            {row.correlationId}
          </span>
        </div>
        {row.errorMessage ? (
          <p className="mt-1 text-biz-sm text-biz-danger font-mono break-words">
            {row.errorMessage}
          </p>
        ) : null}
        <div className="mt-1 text-biz-xs text-biz-muted-foreground">
          {new Date(row.evaluatedAt).toLocaleString()}
          {' · '}
          <span className="biz-numeric">
            {t('evaluations.duration', { ms: row.durationMs })}
          </span>
        </div>
      </div>
      <span className="biz-numeric text-biz-sm font-semibold md:text-right">
        {t(`result.${row.result}` as const)}
      </span>
    </li>
  );
}
